import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useMemberDetail, type HealthStatus } from '@/hooks/useMemberDetail';
import { formatMessageTime } from '@/lib/chatTime';
import type { RosterSidebarGroup } from './sidebar/RosterGroupListItem';
import MemberNotesSection from './MemberNotesSection';

const HEALTH_LABELS: Record<HealthStatus, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'bg-green-100 text-green-700' },
  needs_attention: { label: 'Needs Attention', className: 'bg-amber-100 text-amber-700' },
  at_risk: { label: 'At Risk', className: 'bg-red-100 text-red-700' },
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">{label}</dt>
      <dd className="text-sm text-flock-navy-900">{value}</dd>
    </div>
  );
}

/** Group-context member detail — group + member come from the roster rail routes. */
export default function MemberDetailPage({
  group,
  memberId,
}: {
  group: RosterSidebarGroup;
  memberId: string;
}) {
  const navigate = useNavigate();
  const detailQuery = useMemberDetail(memberId, group.id, group.name);

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Loading member…
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Couldn&apos;t load this member.
      </div>
    );
  }

  const { profile, attendance, groupInfo, isLeader } = detailQuery.data;
  const displayName =
    profile.preferred_name ||
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    profile.email ||
    'Member';
  // Shepherding data is leaders-only. The server already omits `attendance`
  // for non-leaders — the isLeader check here keeps the UI intent explicit.
  const health = isLeader && attendance ? HEALTH_LABELS[attendance.health_status] : null;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-flock-gray-200 bg-white px-6 py-4">
        <button
          onClick={() => navigate(`/roster/group/${group.id}`)}
          className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold text-flock-navy-900">{displayName}</h1>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-4">
          <div className="flex items-center gap-4 rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
            {profile.profile_image_url ? (
              <img
                src={profile.profile_image_url}
                alt=""
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-flock-navy-400 text-xl font-semibold text-white">
                {displayName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join('')}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-flock-navy-900">{displayName}</div>
              <div className="text-xs text-flock-navy-400">
                {groupInfo.group_name} · joined {formatMessageTime(new Date(groupInfo.joined_at))}
              </div>
              <div className="mt-2 flex gap-2">
                {profile.email && (
                  <a
                    href={`mailto:${profile.email}`}
                    className="flex items-center gap-1 rounded-lg border border-flock-gray-200 px-2.5 py-1 text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    <EnvelopeIcon className="h-3.5 w-3.5" /> Email
                  </a>
                )}
                {profile.phone && (
                  <a
                    href={`tel:${profile.phone}`}
                    className="flex items-center gap-1 rounded-lg border border-flock-gray-200 px-2.5 py-1 text-xs text-flock-navy-600 hover:bg-flock-navy-50"
                  >
                    <PhoneIcon className="h-3.5 w-3.5" /> Call
                  </a>
                )}
              </div>
            </div>
            {health && (
              <span
                className={clsx(
                  'rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                  health.className
                )}
              >
                {health.label}
              </span>
            )}
          </div>

          {isLeader && attendance && (
            <div className="rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Shepherding</h2>
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">
                    Health score
                  </dt>
                  <dd className="text-lg font-semibold text-flock-navy-900">
                    {attendance.health_score}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">
                    Attendance
                  </dt>
                  <dd className="text-lg font-semibold text-flock-navy-900">
                    {Math.round(attendance.attendance_rate)}%
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">
                    Absences in a row
                  </dt>
                  <dd className="text-lg font-semibold text-flock-navy-900">
                    {attendance.consecutive_absences}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-flock-navy-400">
                    Last attended
                  </dt>
                  <dd className="text-sm font-medium text-flock-navy-900">
                    {attendance.last_attended_at
                      ? new Date(attendance.last_attended_at).toLocaleDateString()
                      : '—'}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {isLeader && (
            <MemberNotesSection userId={profile.id} groupId={group.id} />
          )}

          <div className="rounded-2xl border border-flock-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-flock-navy-900">Contact & Profile</h2>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Email" value={profile.email} />
              <Field label="Phone" value={profile.phone} />
              <Field label="Preferred contact" value={profile.preferred_contact_method} />
              <Field label="Membership" value={profile.membership_status} />
              <Field label="How they found the church" value={profile.how_found_church} />
              <Field
                label="First visit"
                value={
                  profile.first_visit_date
                    ? new Date(profile.first_visit_date).toLocaleDateString()
                    : null
                }
              />
              <Field label="Emergency contact" value={profile.emergency_contact_name} />
              <Field label="Emergency phone" value={profile.emergency_contact_phone} />
            </dl>
            {profile.bio && <p className="mt-3 text-sm text-flock-navy-800">{profile.bio}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
